import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown, LayoutDashboard, LogOut } from '../../lib/icons';
import { useAuth } from '../../lib/auth';
import { getBranding } from '../../lib/branding';
import { modulesVisibles, estAdmin } from '../../lib/permissions';

const ADMIN = [
  { to: '/admin/utilisateurs', label: 'Utilisateurs', id: 'utilisateurs' },
  { to: '/admin/parametres', label: 'Paramètres', id: 'parametres' },
  { to: '/admin/coherence', label: 'Cohérence trésorerie', id: 'coherence' },
  { to: '/admin/journal', label: "Journal d'activité", id: 'journal' },
];

const itemClass = ({ isActive }) =>
  `flex items-center gap-3 border-l-2 px-4 py-2.5 text-sm transition-colors duration-150 ${
    isActive
      ? 'border-slate-900 bg-slate-100 font-semibold text-slate-900'
      : 'border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900'
  }`;

export const Sidebar = () => {
  const [adminOpen, setAdminOpen] = useState(false);
  const [branding, setBrandingState] = useState(getBranding());
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const maj = () => setBrandingState(getBranding());
    window.addEventListener('branding-maj', maj);
    return () => window.removeEventListener('branding-maj', maj);
  }, []);

  return (
    <aside
      data-testid="sidebar"
      className="flex h-full w-64 flex-shrink-0 flex-col justify-between border-r border-slate-200 bg-white"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex min-h-16 flex-shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-2">
          {branding.logo ? (
            <img
              data-testid="sidebar-logo"
              src={branding.logo}
              alt="Logo de l'huilerie"
              className="h-11 w-11 flex-shrink-0 object-contain"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center bg-slate-900 font-heading text-sm font-black text-white">
              OL
            </div>
          )}
          <div className="min-w-0 leading-tight">
            <p data-testid="sidebar-nom" className="font-heading text-[13px] font-black leading-tight tracking-tight text-slate-900">
              {branding.nom}
            </p>
            <p className="font-data text-[10px] uppercase tracking-[0.12em] text-slate-500">Campagne 2026</p>
          </div>
        </div>

        <nav className="py-3">
          <p className="px-4 pb-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-400">Exploitation</p>
          {modulesVisibles(user?.role).map((m) =>
            m.key === 'dashboard' ? (
              <NavLink key={m.key} end to="/" className={itemClass} data-testid="nav-vue-ensemble">
                <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                <span>Vue d’ensemble</span>
              </NavLink>
            ) : (
              <NavLink key={m.key} to={m.to} className={itemClass} data-testid={`nav-${m.key}`}>
                <span aria-hidden="true">{m.emoji}</span>
                <span>{m.label}</span>
              </NavLink>
            )
          )}
        </nav>
      </div>

      <div className="flex-shrink-0 border-t border-slate-200">
        {estAdmin(user?.role) && (
          <>
            <button
              type="button"
              data-testid="nav-administration-toggle"
              onClick={() => setAdminOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-3 text-sm text-slate-700 transition-colors duration-150 hover:bg-slate-50"
            >
              <span className="flex items-center gap-3">
                <span aria-hidden="true">⚙️</span>
                <span className="font-medium">Administration</span>
              </span>
              <ChevronDown size={15} className={`transition-transform duration-150 ${adminOpen ? 'rotate-180' : ''}`} />
            </button>

            {adminOpen && (
              <div data-testid="admin-submenu" className="border-t border-slate-200 bg-slate-50/70 py-1">
                {ADMIN.map((a) => (
                  <NavLink
                    key={a.id}
                    to={a.to}
                    data-testid={`nav-admin-${a.id}`}
                    className={({ isActive }) =>
                      `block py-2 pl-11 pr-4 text-sm transition-colors duration-150 ${
                        isActive ? 'font-semibold text-slate-900' : 'text-slate-600 hover:text-slate-900'
                      }`
                    }
                  >
                    {a.label}
                  </NavLink>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <p data-testid="sidebar-user-name" className="truncate text-sm font-semibold text-slate-900">
              {user?.name}
            </p>
            <p className="font-data text-[10px] uppercase tracking-[0.1em] text-slate-500">{user?.role}</p>
          </div>
          <button
            type="button"
            data-testid="logout-button"
            title="Déconnexion"
            onClick={() => {
              signOut();
              navigate('/login');
            }}
            className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
};
